<script setup lang="ts">

import { faArrowDown, faArrowUp, faRectangleVertical, faTableColumns, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'
import { IconContainer } from '@shared/ui/other'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useScreenList } from '../../../model/assessments/screen-list.hook'

defineOptions({
  name: 'ScreenList',
})

const {
  refScreens,
  refHasScreens,
  selectScreen,
  addScreen,
  removeScreen,
  moveScreenUp,
  moveScreenDown,
} = useScreenList()
</script>

<template>
  <div class="screen-list">
    <AppText name="body1-medium">
      Screens
    </AppText>

    <AppText
      v-if="!refHasScreens"
      name="body2"
      color="slate-500"
    >
      No screens yet. Add the first one to start authoring.
    </AppText>

    <ul class="screen-list__items">
      <li
        v-for="screen in refScreens"
        :key="screen.id"
        :class="['screen-list__item', { 'is-selected': screen.isSelected }]"
      >
        <!-- Title row, controls pinned to the top right: they act on the screen as a whole, so
             they belong beside its name rather than under the text that describes it. -->
        <div class="screen-list__head">
          <button
            type="button"
            class="screen-list__select"
            @click="selectScreen(screen.id)"
          >
            <span class="screen-list__title">
              <AppText
                name="body2-medium"
                truncate
              >
                {{ screen.label }}
              </AppText>
              <!-- One column or two, as an icon: the layout is a property of the screen the
                   author picks in the form, and this is where they see what they picked. -->
              <IconContainer
                class="screen-list__layout"
                :title="screen.layoutLabel"
              >
                <FontAwesomeIcon
                  :icon="screen.layout === 'two-column' ? faTableColumns : faRectangleVertical"
                />
              </IconContainer>
            </span>
            <AppText
              name="body2"
              color="slate-500"
              truncate
            >
              {{ screen.summary }}
            </AppText>
            <AppText
              name="body2"
              color="slate-500"
            >
              {{ screen.metaLabel }}
            </AppText>
          </button>
          <div class="screen-list__actions">
            <BtnIcon
              :icon="faArrowUp"
              size="small"
              title="Move screen up"
              :disabled="screen.isFirstInOrder"
              @click="moveScreenUp(screen.id)"
            />
            <BtnIcon
              :icon="faArrowDown"
              size="small"
              title="Move screen down"
              :disabled="screen.isLastInOrder"
              @click="moveScreenDown(screen.id)"
            />
            <BtnIcon
              :icon="faTrashCan"
              size="small"
              color="red"
              title="Remove screen"
              @click="removeScreen(screen.id)"
            />
          </div>
        </div>
      </li>
    </ul>

    <BtnSecondary
      text="Add screen"
      element-width="full"
      @click="addScreen"
    />
  </div>
</template>

<style scoped lang="scss" src="./ScreenList.scss" />
